/**
 * Getting Mapbox GL into the page, once.
 *
 * Lifted out of CrmCallMap when a second screen needed a map. Two
 * copies of this would be two copies of the retry handling below, and
 * the one that did not get the fix is the one that leaves somebody
 * looking at a silent grey box.
 *
 * Served from /vendor rather than Mapbox's CDN: the app is installable
 * and the map has to come up on a connection that may only reach us.
 */
export const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN
  || import.meta.env.VITE_MAPBOX_ACCESS_TOKEN
  || '';
export const MAPBOX_STYLE = import.meta.env.VITE_MAPBOX_STYLE
  || 'mapbox://styles/mapbox/light-v11';

const SCRIPT_ID = 'crm-mapbox-gl-js';
const CSS_ID = 'crm-mapbox-gl-css';
const SCRIPT_SRC = '/vendor/mapbox-gl.js';
const CSS_HREF = '/vendor/mapbox-gl.css';
const LOAD_TIMEOUT_MS = 15000;

export function loadMapboxGl() {
  if (typeof window === 'undefined') return Promise.reject(new Error('Mapbox GL requires a browser'));
  if (window.mapboxgl) return Promise.resolve(window.mapboxgl);

  if (!document.getElementById(CSS_ID)) {
    const link = document.createElement('link');
    link.id = CSS_ID;
    link.rel = 'stylesheet';
    link.href = CSS_HREF;
    document.head.appendChild(link);
  }

  return new Promise((resolve, reject) => {
    // A script tag left behind by an earlier failed or hung load never
    // fires its events again — waiting on it kept the map a silent grey
    // box forever. Start from a fresh tag every time the library isn't
    // actually present, and give up loudly instead of never.
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      if (existing.dataset.loaded === 'true' && window.mapboxgl) {
        resolve(window.mapboxgl);
        return;
      }
      existing.remove();
    }
    const script = document.createElement('script');
    const timer = setTimeout(() => {
      reject(new Error('The map engine did not load in time — check your connection and reload the page.'));
    }, LOAD_TIMEOUT_MS);
    script.addEventListener('load', () => {
      clearTimeout(timer);
      script.dataset.loaded = 'true';
      if (window.mapboxgl) resolve(window.mapboxgl);
      else reject(new Error('Mapbox GL did not initialize'));
    }, { once: true });
    script.addEventListener('error', () => {
      clearTimeout(timer);
      script.remove();
      reject(new Error('Could not load the map engine (/vendor/mapbox-gl.js). Reload the page to retry.'));
    }, { once: true });
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    document.head.appendChild(script);
  });
}
